<script setup>
import maplibregl from 'maplibre-gl'
import { Protocol } from 'pmtiles'
import 'maplibre-gl/dist/maplibre-gl.css'
import { getSmartPlatform } from '@weathernews/btos-javascript-modules/src/wniutil/common.js'
import { CustomAttributionControl } from '~/utils/customAttribution'

const route = useRoute()
const config = useRuntimeConfig()
const nowUrl = route.path
const map = shallowRef(null)
const mapContainer = ref(null)
const golfPointName = ref('')
const runtimeConfig = useRuntimeConfig()
const { $isWebViewFlag: isWebViewFlag } = useNuxtApp()

const props = defineProps({
  mapData: {
    type: Array,
    default: () => []
  },
  latlon: {
    type: Object,
    default: () => {}
  },
  golfJson: {
    type: Object,
    default: () => {}
  },
  mapName: {
    type: String,
    default: ''
  }
})
const addGeoJsonMarkers = async (golfJson) => {
  const [activeImage, inActiveImage] = await Promise.all([
    map.value.loadImage('https://gvs.weathernews.jp/golf/image/map/golf_icon1_50.png'),
    map.value.loadImage('https://gvs.weathernews.jp/golf/image/map/golf_icon2_50.png')
  ])
  map.value.addImage('golf-marker-active', activeImage.data)
  map.value.addImage('golf-marker-inactive', inActiveImage.data)
  golfJson.features.forEach((feature) => {
    feature.properties.active = feature.properties.pref_code === props.mapName.toUpperCase() ? 'active' : 'inactive'
  })
  map.value.addSource('golf-points', {
    type: 'geojson',
    data: golfJson
  })
  map.value.addLayer({
    id: 'golf-points',
    type: 'symbol',
    source: 'golf-points',
    layout: {
      'icon-image': ['concat', 'golf-marker-', ['get', 'active']],
      'icon-size': ['match', ['get', 'active'], 'active', 0.5, 0.4],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'symbol-sort-key': ['match', ['get', 'active'], 'active', 1, 0]
    }
  })
  map.value.addLayer({
    id: 'golf-points-labels',
    type: 'symbol',
    source: 'golf-points',
    minzoom: 11,
    layout: {
      'text-field': ['get', 'name'],
      'text-size': 12,
      'text-anchor': 'top',
      'text-offset': [0, 1],
      'text-allow-overlap': false,
      'text-ignore-placement': false,
      'text-max-width': 8,
      'visibility': 'visible'
    },
    paint: {
      'text-color': '#000000',
      'text-halo-color': '#ffffff',
      'text-halo-width': 1.5,
      'text-opacity': [
        'interpolate',
        ['linear'],
        ['zoom'],
        10, 0,
        11, 1
      ]
    }
  })
  map.value.on('mouseenter', 'golf-points', (e) => {
    if (e.features.length > 0) {
      map.value.getCanvas().style.cursor = 'pointer'
      golfPointName.value = e.features[0].properties.name
    }
  })
  map.value.on('mouseleave', 'golf-points', () => {
    map.value.getCanvas().style.cursor = ''
    golfPointName.value = ''
  })
  map.value.on('mousemove', 'golf-points', (e) => {
    if (e.features.length > 0) {
      const properties = e.features[0].properties
      golfPointName.value = properties.name
      map.value.getCanvas().style.cursor = 'pointer'
    }
  })
  map.value.on('click', 'golf-points', async (e) => {
    if (e.features.length > 0) {
      const properties = e.features[0].properties
      await (isWebViewFlag ? navigateTo(`${runtimeConfig.app.baseURL}${properties.path.replace(/^\//, '')}`, { external: true }) : navigateTo(`${properties.path}`))
    }
  })
}

const initMap = async (isMobile) => {
  let attributionControl = null
  const protocol = new Protocol()
  maplibregl.addProtocol('pmtiles', protocol.tile)
  const currentZoom = isMobile ? props.latlon.zoom - 1 : props.latlon.zoom
  map.value = new maplibregl.Map({
    container: mapContainer.value,
    style: `${config.public.baseMapDomain}/style/global/osm/basic-ja.json`,
    localIdeographFontFamily: '"Noto Sans", "Noto Sans CJK SC", sans-serif',
    center: [props.latlon.lon, props.latlon.lat],
    zoom: currentZoom,
    minZoom: 6,
    maxZoom: 14,
    attributionControl: false,
    touchPitch: false
  })
  map.value.dragRotate.disable()
  map.value.keyboard.disable()
  map.value.touchZoomRotate.disableRotation()
  map.value.doubleClickZoom.disable()
  map.value.addControl(new maplibregl.NavigationControl({
    showCompass: false,
    showZoom: true
  }), 'top-right')
  attributionControl = new CustomAttributionControl({
    compact: true
  })
  map.value.addControl(attributionControl, 'bottom-left')
  await map.value.once('load', () => {
    const osmSource = map.value.getSource('osm')
    const attribution = osmSource.attribution
    if (attribution) {
      attributionControl.updateCustomAttributions([attribution])
    }
    map.value.addSource('prefecture-boundaries', {
      type: 'vector',
      url: `${config.public.baseMapDomain}/json/japan/admin/latest.json`
    })
    map.value.addLayer({
      'id': 'prefecture-boundaries-line',
      'type': 'line',
      'source': 'prefecture-boundaries',
      'source-layer': 'pref',
      'filter': ['==', '$type', 'Polygon'],
      'minzoom': 6,
      'maxzoom': 12,
      'paint': {
        'line-width': 1,
        'line-color': '#606060'
      }
    })
    addGeoJsonMarkers(props.golfJson)
  })
}
onMounted(async () => {
  if (props.mapData.length === 0) {
    const isMobile = getSmartPlatform() !== ''
    await initMap(isMobile)
  }
})
onUnmounted(() => {
  if (map.value) {
    map.value.off('mouseenter', 'golf-points')
    map.value.off('mouseleave', 'golf-points')
    map.value.off('mousemove', 'golf-points')
    map.value.off('click', 'golf-points')
  }
})
</script>

<template>
  <section class="golfMap">
    <CommonPageTitle :title="`${getPrefectureName(mapName) ? `${getPrefectureName(mapName)}の` : ''}ゴルフ場の天気`" />
    <template v-if="mapData.length === 0">
      <div class="map">
        <div
          v-if="golfPointName"
          class="popup"
        >
          <p>
            {{ golfPointName }}
          </p>
        </div>
        <figure>
          <div
            ref="mapContainer"
            class="mapContainer"
          />
        </figure>
      </div>
    </template>
    <template v-else>
      <div class="map">
        <figure>
          <img
            :src="`https://gvs.weathernews.jp/golf/image/map/${mapName}.webp`"
            alt=""
          >
        </figure>
        <NuxtLink
          v-for="areas in mapData"
          :key="areas.key"
          :to="`${isWebViewFlag ? `${runtimeConfig.app.baseURL}${nowUrl.replace(/^\//, '')}${areas.key}/` : `${nowUrl}${areas.key}/`}`"
          class="link"
          :data-region="areas.key"
          :external="isWebViewFlag"
        >
          <span>{{ areas.name }}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 14 18"
          >
            <path
              d="M0.999999 16.5L11 9L1 1.5"
              stroke-width="3"
            />
          </svg>
        </NuxtLink>
      </div>
    </template>
  </section>
</template>

<style>
.maplibregl-ctrl-attrib {
  display: flex;
  align-items: center;
}
.maplibregl-ctrl-attrib-button {
  top: auto;
}
.maplibregl-ctrl-attrib-inner a {
  font-size: 12px;
}
</style>

<style lang="scss" scoped>
.golfMap {
  display: flex;
  flex-direction: column;
  gap: 10px;
  :deep(.maplibregl-marker) {
    cursor: pointer;
  }
  .map {
    position: relative;
    .popup {
      position: absolute;
      top: 5px;
      left: 5px;
      z-index: 1;
      background-color: #fff;
      padding: 2px 5px;
      border-radius: 5px;
      box-shadow: 0 2px 4px 0 rgb(0 0 0 / .25);
      p {
        font-size: 12px;
        font-weight: 600;
      }
    }
    figure {
      display: flex;
      gap: 10px;
      width: 100%;
      aspect-ratio: 39 / 23;
      img {
        width: 100%;
      }
      .mapContainer {
        width: 100%;
        height: 100%;
      }
    }
  }
  .link {
    position: absolute;
    display: flex;
    align-items: center;
    gap: min(0.8vw, 3px);
    padding: min(0.8vw, 3px) min(3.2vw, 12px);
    border-radius: 100vmax;
    border: 2px solid #268600;
    background-color: #fff;
    box-shadow: 0 4px 4px 0 rgb(0 0 0 / .25);
    &[data-region="hokkaido"] {
      bottom: 80%;
      right: 30%;
    }
    &[data-region="tohoku"] {
      bottom: 50%;
      left: 60%;
    }
    &[data-region="kanto"] {
      top: 60%;
      left: 62%;
    }
    &[data-region="chubu"] {
      bottom: 40%;
      right: 44%;
    }
    &[data-region="kinki"] {
      bottom: 18%;
      left: 42%;
    }
    &[data-region="chugoku"] {
      bottom: 28%;
      right: 61%;
    }
    &[data-region="shikoku"] {
      top: 82%;
      left: 30%;
    }
    &[data-region="kyushu"] {
      top: 80%;
      right: 73%;
    }
    &[data-region="okinawa"] {
      bottom: 82%;
      right: 77%;
    }
    &[data-region="douou"] {
      bottom: 40%;
      right: 65%;
    }
    &[data-region='dounan'] {
      top: 75%;
      left: 35%;
    }
    &[data-region='douhoku'] {
      top: 15%;
      right: 50%;
    }
    &[data-region='doutou'] {
      top: 45%;
      left: 60%;
    }
    &[data-region='aomori'] {
      bottom: 75%;
      left: 55%;
    }
    &[data-region='iwate'] {
      bottom: 53%;
      left: 58%;
    }
    &[data-region='akita'] {
      bottom: 55%;
      right: 50%;
    }
    &[data-region='miyagi'] {
      top: 62%;
      left: 55%;
    }
    &[data-region='yamagata'] {
      bottom:33%;
      right: 50%;
    }
    &[data-region='fukushima'] {
      top: 82%;
      left: 50%;
    }
    &[data-region='ibaraki'] {
      top: 35%;
      left: 63%;
    }
    &[data-region='tochigi'] {
      top: 18%;
      left: 47%;
    }
    &[data-region='gunma'] {
      top: 24%;
      right: 58%;
    }
    &[data-region='saitama'] {
      bottom: 45%;
      right: 50%;
    }
    &[data-region='chiba'] {
      top: 66%;
      left: 62%;
    }
    &[data-region='tokyo'] {
      top: 60%;
      right: 47%;
    }
    &[data-region='kanagawa'] {
      top: 75%;
      right: 50%;
    }
    &[data-region='niigata'] {
      top: 20%;
      left: 58%;
    }
    &[data-region='toyama'] {
      top: 40%;
      right: 53%;
    }
    &[data-region='ishikawa'] {
      top: 25%;
      right: 56%;
    }
    &[data-region='fukui'] {
      top: 58%;
      right: 70%;
    }
    &[data-region='yamanashi'] {
      top: 66%;
      left: 53%;
    }
    &[data-region='nagano'] {
      top: 45%;
      left: 52%;
    }
    &[data-region='gifu'] {
      top: 55%;
      left: 34%;
    }
    &[data-region='shizuoka'] {
      top: 81%;
      left: 49%;
    }
    &[data-region='aichi'] {
      top: 70%;
      right: 50%;
    }
    &[data-region='mie'] {
      top: 83%;
      right: 63%;
    }
    &[data-region='shiga'] {
      top: 28%;
      left: 53%;
    }
    &[data-region='kyoto'] {
      bottom: 72%;
      left: 40%;
    }
    &[data-region='osaka'] {
      top: 48%;
      right: 52%;
    }
    &[data-region='hyogo'] {
      top: 31%;
      left: 27%;
    }
    &[data-region='nara'] {
      top: 50%;
      left: 48%;
    }
    &[data-region='wakayama'] {
      top: 76%;
      right: 51%;
    }
    &[data-region='tottori'] {
      bottom: 58%;
      left: 64%;
    }
    &[data-region='shimane'] {
      bottom: 50%;
      right: 53%;
    }
    &[data-region='okayama'] {
      top: 58%;
      left: 60%;
    }
    &[data-region='hiroshima'] {
      top: 65%;
      right: 45%;
    }
    &[data-region='yamaguchi'] {
      top: 77%;
      right: 71%;
    }
    &[data-region='kagawa'] {
      top: 20%;
      left: 55%;
    }
    &[data-region='tokushima'] {
      top: 40%;
      left: 60%;
    }
    &[data-region='ehime'] {
      top: 38%;
      right: 61%;
    }
    &[data-region='kouchi'] {
      top: 50%;
      right: 45%;
    }
    &[data-region='fukuoka'] {
      bottom: 75%;
      left: 50%;
    }
    &[data-region='saga'] {
      bottom: 64%;
      right: 48%;
    }
    &[data-region='nagasaki'] {
      top: 37%;
      right: 60%;
    }
    &[data-region='oita'] {
      bottom: 61%;
      left: 58%;
    }
    &[data-region='kumamoto'] {
      bottom: 47%;
      left: 44%;
    }
    &[data-region='miyazaki'] {
      top: 56%;
      left: 55%;
    }
    &[data-region='kagoshima'] {
      top: 70%;
      right: 50%;
    }
    svg {
      width: min(2.7vw, 10px);
      fill: none;
    }
    path {
      stroke: #268600;
    }
    span {
      color: #000;
      font-size: min(4vw, 15px);
      font-weight: 600;
      line-height: min(4.8vw, 18px);
      white-space: nowrap;
    }
  }
  @media (any-hover: hover) {
    .link {
      transition: .2s ease-in-out;
      path {
        transition: stroke .2s ease-in-out;
      }
      span {
        transition: color .2s ease-in-out;
      }
      &:hover {
        background-color: #268600;
        border-color: #fff;
        box-shadow: 0 4px 4px 2px rgb(0 0 0 / .5);
        path {
          stroke: #fff;
        }
        span {
          color: #fff;
        }
      }
    }
  }
}
@media screen and (min-width: 830px) {
  .golfMap {
    .map {
      .popup {
        p {
          font-size: 16px;
          font-weight: 600;
        }
      }
      .link {
        gap: min(0.56vw, 6px);
        padding: min(0.56vw, 6px) min(1.68vw, 18px);
        &[data-region='nagano'] {
          left: 48%;
        }
        &[data-region='mie'] {
          top: 85%;
        }
        svg {
          width: min(1.31vw, 14px);
        }
        span {
          font-size: min(1.87vw, 20px);
          line-height: min(2.06vw, 22px);
        }
      }
    }
  }
}
</style>
